/* eslint-disable no-console */
/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { sortBy, startCase } from "lodash";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import Alert from "@shared/v2/Alert";
import { responseDataToCamelCase } from "@shared/v2/caseTransformingAxios";
import Dropdown from "@shared/v2/Dropdown/Dropdown";
import ConnectServicesActivationModal, {
  useConnectServicesActivationModal,
} from "@shared/ConnectServicesActivationModal";

import Button from "@shared/v2/Button";
import {
  ArrowUpRightFromSquareSolidV6,
  ArrowUpRightSolidV6,
  ChevronDownSolidV6,
  PaperPlaneSolidV6,
  Spinner,
  XmarkSolidV6,
} from "@shared/v2/Icomoon";
import apiClient from "../../Main/Details/api/apiClient";

import Header from "../../UI/Header";
import ConnectServicesPromo from "./ConnectServicesPromo";
import useRequestConnectedServiceReview from "./useRequestConnectedServiceReview";
import { useAccountProducts } from "../../../reducers/layoutReducer/selectors";
import { setConnectServiceActive } from "../../../reducers/layoutReducer";

const MISSING_INFO_LABELS = {
  email: "Client Email",
  phone: "Client Phone",
  close_date: "Close Date",
  listing_address: "Full Transaction Address",
};

const STATE_CONFIG = {
  INFO_NEEDED: { text: "Info Needed", txtColor: "tw-text-[#FA423C]" },
  IN_QUEUE: { text: "In Queue", txtColor: "tw-text-[#7532FA]" },
  REFERRAL_SENT: { text: "Referral Sent", txtColor: "tw-text-yellow-100" },
  NOT_ELIGIBLE: { text: "Not Eligible", txtColor: "tw-text-gray-50" },
  DISABLED: { text: "Disabled", txtColor: "tw-text-gray-50" },
  CANCELED: { text: "Canceled", txtColor: "tw-text-gray-50" },
  WITHDRAWN: { text: "Canceled", txtColor: "tw-text-gray-50" },
};

const ASSISTED_STATES = ["customer", "utilities_only"];
const DECLINED_STATES = ["declined", "request_no_contact", "changed_mind"];
const NOT_NEEDED_STATES = ["self_setup", "past_move_in_date", "unable_to_assist", "no_customer"];
const REFERRAL_FAILED_STATES = ["bad_data", "duplicate"];

const sharedCard = (outcomes, card) => Object.fromEntries(outcomes.map((outcome) => [outcome, card]));

// Connect Services' remote outcome supersedes our local status once it maps to a card.
const REMOTE_OUTCOME_CONFIG = {
  new: STATE_CONFIG.REFERRAL_SENT,
  contacting: { text: "Being Assisted", txtColor: "tw-text-green-100" },
  working: { text: "Being Assisted", txtColor: "tw-text-green-100" },
  customer: { text: "Assisted", txtColor: "tw-text-theme-brand" },
  utilities_only: { text: "Assisted", txtColor: "tw-text-theme-brand" },
  cancelled: STATE_CONFIG.CANCELED,
  unresponsive: { text: "Not Contacted", txtColor: "tw-text-gray-30" },
  ...sharedCard(DECLINED_STATES, { text: "Declined", txtColor: "tw-text-gray-30" }),
  ...sharedCard(NOT_NEEDED_STATES, { text: "Not Needed", txtColor: "tw-text-gray-30" }),
  ...sharedCard(REFERRAL_FAILED_STATES, { text: "Referral Failed", txtColor: "tw-text-semantic-red-110" }),
};

const ConnectHeader = () => (
  <Header
    header="Connect Services"
    add={
      <ArrowUpRightFromSquareSolidV6
        className="tw-text-[#2E6CE5] tw-ml-[6px] tw-cursor-pointer"
        size="m"
        onClick={() =>
          window.open("https://partner.connectservices.com/prm/register-a-referral-place", "_blank")
        }
      />
    }
  />
);

const ConnectServices = ({
  listingUuid = "",
  transactionType = null,
  openDisableAutomatedSendModal = () => {},
  openEnableAutomatedSendModal = () => {},
  connectedService = null,
  setConnectedService = () => {},
  connectServicesError = false,
  setConnectServicesError = () => {},
  clients = [],
  clientRoles = [],
}) => {
  const { connectService } = useAccountProducts();
  const { isOpen, open, close } = useConnectServicesActivationModal();
  const dispatch = useDispatch();
  const {
    requestCallback: requestReview,
    error: reviewError,
    isSubmitting: isRequestingReview,
  } = useRequestConnectedServiceReview();
  const isEligibleType = transactionType === "buyer" || transactionType === "tenant";

  const scheduledDate = connectedService?.scheduledSendAt;
  const scheduledIsToday = scheduledDate && dayjs(scheduledDate).isSame(dayjs(), "day");
  const sendErrorMessage = scheduledIsToday
    ? "We couldn't send this referral right now. It will be retried automatically tomorrow."
    : `We couldn't send this referral right now. It is still scheduled to send automatically on ${dayjs(scheduledDate).format("MM/DD/YYYY")}.`;

  const clientOptions = useMemo(() => {
    if (!clients?.length || !clientRoles?.length) return [];
    return sortBy(
      clientRoles
        .map((role) => {
          const client = (clients || []).find((c) => c.role?.id === role.id);
          if (!client) return null;
          return {
            label: `${role.title} (${client.person.name} ${client.person.email_address})`,
            displayLabel: role.title,
            value: client.id,
          };
        })
        .filter(Boolean),
      "label",
    );
  }, [clients, clientRoles]);

  const [isFetching, setIsFetching] = useState(Boolean(listingUuid));

  useEffect(() => {
    if (!listingUuid || !connectService) return;
    setIsFetching(true);
    setConnectServicesError(false);
    apiClient
      .getConnectedService(listingUuid)
      .then((res) => setConnectedService(res.data))
      .catch((err) => console.error("Failed to fetch connected service:", err))
      .finally(() => setIsFetching(false));
  }, [listingUuid, connectService]);

  const remoteOutcome = connectedService?.remoteOutcome;
  const state = REMOTE_OUTCOME_CONFIG[remoteOutcome] ? remoteOutcome : connectedService?.status;
  const stateConfig = REMOTE_OUTCOME_CONFIG[state] || STATE_CONFIG[state];

  if (!connectService) {
    return (
      <>
        <ConnectServicesPromo onGetStarted={open} />
        <ConnectServicesActivationModal
          show={isOpen}
          onCancel={close}
          onActivated={() => dispatch(setConnectServiceActive())}
        />
      </>
    );
  }

  if (isFetching || transactionType === null) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-[16px]">
        <ConnectHeader />
        <div
          className="tw-flex tw-min-h-[60px] tw-items-center tw-justify-center"
          role="status"
          aria-label="Loading Connect Services"
        >
          <Spinner size="xxxl" className="tw-animate-spin tw-text-neutral-gray-50" />
        </div>
      </div>
    );
  }

  if (!isEligibleType) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-[16px]">
        <ConnectHeader />
        <p className="tw-text-14d tw-font-semibold tw-leading-[20px] tw-text-gray-50">
          Connect services referral automation is only available for Buyer and Tenant transactions.
        </p>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col">
      {connectServicesError && (
        <div className="tw-mb-[12px]">
          <Alert variant="error" text={sendErrorMessage} />
        </div>
      )}
      <div className="tw-flex tw-items-center tw-mb-[16px]">
        <ConnectHeader />
        {stateConfig?.text && (
          <div className={`${stateConfig.txtColor} tw-text-11d tw-ml-auto tw-font-bold tw-uppercase`}>
            {stateConfig.text}
          </div>
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-gap-[4px]">
        {state === "IN_QUEUE" && (
          <div className="tw-flex tw-items-center tw-justify-between tw-h-[20px]">
            <div className="tw-text-14d">Scheduled to send on</div>
            <Dropdown
              placeholder="Select"
              menuShouldComeToFront
              options={[
                {
                  label: (
                    <div>
                      <PaperPlaneSolidV6 className="tw-mr-[4px]" />
                      <span>Send Info Now</span>
                    </div>
                  ),
                  value: "send",
                },
                {
                  label: (
                    <div>
                      <XmarkSolidV6 className="tw-mr-[4px]" />
                      <span>Disable Send</span>
                    </div>
                  ),
                  value: "disable",
                },
              ]}
              name="connectServicesRole"
              value={{
                label: connectedService.scheduledSendAt
                  ? dayjs(connectedService.scheduledSendAt).format("MM/DD/YYYY")
                  : "N/A",
              }}
              onChange={(option) => {
                if (option.value === "disable") {
                  openDisableAutomatedSendModal();
                }
                if (option.value === "send") {
                  setConnectServicesError(false);
                  apiClient
                    .sendConnectedServiceNow(listingUuid)
                    .then((res) => setConnectedService(res.data))
                    .catch((err) => {
                      if (err?.response?.status === 502 && err.response.data) {
                        setConnectedService(responseDataToCamelCase(err.response.data));
                        setConnectServicesError(true);
                      }
                    });
                }
              }}
              components={{
                DropdownIndicator: () => (
                  <ChevronDownSolidV6
                    size="s"
                    className="tw-text-gray-75 tw-ml-[6px] tw-font-semibold tw-mr-[2px]"
                  />
                ),
              }}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: "auto",
                }),
                control: (provided) => ({
                  ...provided,
                  minHeight: "auto",
                  width: "max-content",
                  border: "none",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  padding: "0",
                  cursor: "pointer",
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  padding: "0",
                  fontWeight: "600",
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: "var(--neutral-gray-75)",
                  position: "static",
                  transform: "none",
                  maxWidth: "none",
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  padding: "0",
                  color: "var(--neutral-gray-50)",
                }),
                menu: (provided) => ({
                  ...provided,
                  width: "193px",
                  minWidth: "100%",
                  transform: "translateX(-143px)",
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
          </div>
        )}
        {(state === "INFO_NEEDED" || state === "IN_QUEUE") && (
          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="tw-text-14d">Send contact info for</div>
            <Dropdown
              placeholder="Select"
              menuShouldComeToFront
              options={clientOptions}
              name="connectServicesClient"
              value={
                clientOptions.find((o) => o.displayLabel === connectedService?.sendContactInfoFor) ||
                clientOptions[0] ||
                null
              }
              getOptionLabel={(o) => o.label}
              formatOptionLabel={(o, { context }) => (context === "value" ? o.displayLabel : o.label)}
              onChange={(option) => {
                apiClient
                  .patchConnectedService(listingUuid, { listing_person_id: option.value })
                  .then((res) => setConnectedService(res.data))
                  .catch(() => {});
              }}
              components={{
                DropdownIndicator: () => (
                  <ChevronDownSolidV6
                    size="s"
                    className="tw-text-gray-75 tw-ml-[6px] tw-font-semibold tw-mr-[2px]"
                  />
                ),
              }}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: "auto",
                }),
                control: (provided) => ({
                  ...provided,
                  minHeight: "auto",
                  width: "max-content",
                  border: "none",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  padding: "0",
                  cursor: "pointer",
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  padding: "0",
                  fontWeight: "600",
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: "var(--neutral-gray-75)",
                  position: "static",
                  transform: "none",
                  maxWidth: "none",
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  padding: "0",
                  color: "var(--neutral-gray-50)",
                }),
                menu: (provided) => ({
                  ...provided,
                  width: "max-content",
                  minWidth: "100%",
                  right: 0,
                  left: "auto",
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
          </div>
        )}
        {state === "INFO_NEEDED" && (
          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <div className="tw-text-12d">
              Please enter all the required information in order to share this contact info with Connect
              Services.
            </div>
            {connectedService?.missingInfo?.length > 0 && (
              <ul className="tw-list-disc tw-pl-[20px] tw-text-12d">
                {connectedService?.missingInfo?.map((requirement) => (
                  <li key={requirement}>{MISSING_INFO_LABELS[requirement] || startCase(requirement)}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {state === "IN_QUEUE" && (
          <div className="tw-text-12d tw-font-semibold tw-text-gray-50">
            Referral will automatically be sent 21 days before the close date.{" "}
          </div>
        )}
        {(state === "REFERRAL_SENT" || state === "new") && (
          <>
            <div className="tw-flex tw-items-center tw-justify-between tw-h-[20px]">
              <div className="tw-text-14d">Contact info sent on</div>
              <div className="tw-font-semibold tw-text-14d tw-text-gray-75">
                {connectedService?.referralSentAt
                  ? dayjs(connectedService.referralSentAt).format("MM/DD/YYYY")
                  : "N/A"}
              </div>
            </div>
            <div className="tw-text-12d tw-font-semibold tw-text-gray-50">
              Connect Services is reaching out to your client.
            </div>
          </>
        )}
        {(state === "contacting" || state === "working") && (
          <div className="tw-text-14d tw-text-gray-75">
            Connect Services is actively working with your client based on their unique situation and
            coverage.
          </div>
        )}
        {ASSISTED_STATES.includes(state) && (
          <>
            {connectedService?.researchEmailDatetime && (
              <div className="tw-flex tw-items-center tw-justify-between tw-h-[20px]">
                <div className="tw-text-14d">Utilities Research Provided</div>
                <div className="tw-font-semibold tw-text-14d tw-text-gray-75">
                  {dayjs(connectedService.researchEmailDatetime).format("MM/DD/YYYY")}
                </div>
              </div>
            )}
            <div className="tw-text-12d tw-font-semibold tw-text-gray-50">
              Your client&apos;s move-in needs have been addressed through Connect Services.
            </div>
          </>
        )}
        {state === "NOT_ELIGIBLE" && (
          <div className="tw-text-12d tw-font-semibold tw-text-gray-50">
            Info for this property cannot be sent to Connect Services because it did not have all required
            info or the close date was before today’s date.{" "}
          </div>
        )}
        {(state === "CANCELED" || state === "WITHDRAWN") && (
          <div className="tw-text-12d tw-font-semibold tw-text-gray-50">
            {connectedService?.referralSentAt
              ? "Connect Services has been notified of this transaction being canceled and will close communication with your client."
              : "This transaction was canceled before contact info was sent to Connect Services."}
          </div>
        )}
        {state === "cancelled" && (
          <div className="tw-text-12d tw-text-gray-50">
            Connect Services has been notified of this transaction being canceled and will close communication
            with your client.
          </div>
        )}
        {state === "unresponsive" && (
          <div className="tw-text-14d tw-text-gray-75">
            Connect Services hasn&apos;t been able to reach your client.
          </div>
        )}
        {DECLINED_STATES.includes(state) && (
          <div className="tw-text-14d tw-text-gray-75">Your client chose not to use Connect Services.</div>
        )}
        {NOT_NEEDED_STATES.includes(state) && (
          <div className="tw-text-14d tw-text-gray-75">
            Your client didn&apos;t end up needing assistance from Connect Services.
          </div>
        )}
        {REFERRAL_FAILED_STATES.includes(state) && (
          <>
            <div className="tw-text-14d tw-text-gray-75">
              Connect Services could not process this lead due to bad or invalid information.
            </div>
            <Button
              schema="secondary"
              onClick={() => requestReview(listingUuid)}
              isLoading={isRequestingReview}
              className="tw-mt-[12px] tw-w-full tw-h-[44px] !tw-rounded-[8px] tw-flex !tw-gap-[4px] tw-items-center tw-justify-center tw-text-14d !tw-normal-case"
            >
              <span className="tw-mr-[4px]">Ask to Review</span>
              <ArrowUpRightSolidV6 />
            </Button>
            {reviewError && (
              <div className="tw-mt-[12px]">
                <Alert variant="error" text={reviewError} />
              </div>
            )}
          </>
        )}
        {state === "DISABLED" && (
          <>
            <div className="tw-gray-50 tw-text-14d tw-font-semibold tw-text-gray-50">
              Reason and Date Disabled
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-h-[20px]">
              <div className="tw-text-14d">&quot;{connectedService.disabledReason}&quot;</div>
            </div>
            <div className="tw-text-12d tw-font-semibold tw-text-gray-50">
              Info for this property will not be sent to Connect Services.
            </div>
            <div className="tw-mt-[20px]">
              <Button
                schema="secondary"
                onClick={openEnableAutomatedSendModal}
                className="tw-mt-[4px] tw-w-full tw-h-[44px] !tw-rounded-[8px] tw-flex !tw-gap-[4px] tw-items-center tw-justify-center tw-text-14d !tw-normal-case"
              >
                <span className="tw-mr-[4px]">Refer to Connect Services</span>
                <ArrowUpRightSolidV6 />
              </Button>
            </div>
          </>
        )}
        {connectedService === null && (
          <div className="tw-flex tw-flex-col tw-gap-[4px]">
            <div className="tw-text-14d tw-font-semibold tw-text-gray-50">
              Utilities and home services concierge
            </div>
            <div className="tw-text-12d tw-font-semibold tw-text-gray-50">
              You can automatically refer clients when transactions go pending.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ConnectServices.propTypes = {
  listingUuid: PropTypes.string,
  transactionType: PropTypes.string,
  openDisableAutomatedSendModal: PropTypes.func,
  openEnableAutomatedSendModal: PropTypes.func,
  connectedService: PropTypes.shape({
    status: PropTypes.string,
    scheduledSendAt: PropTypes.string,
    missingInfo: PropTypes.arrayOf(PropTypes.string),
    disabledReason: PropTypes.string,
    referralSentAt: PropTypes.string,
    sendContactInfoFor: PropTypes.string,
    researchEmailDatetime: PropTypes.string,
    remoteOutcome: PropTypes.string,
  }),
  setConnectedService: PropTypes.func,
  connectServicesError: PropTypes.bool,
  setConnectServicesError: PropTypes.func,
  clients: PropTypes.arrayOf(PropTypes.shape({})),
  clientRoles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
};

export default ConnectServices;
